import {
  CartRequest,
  CartResponse,
  Product,
  CartItemRequirementType,
} from "src/interfaces";

import useOrder from "./useOrder";
import { getItemRequirements } from "@Utils/formUtils";
import React from "react";
import { ISessionClient } from "@Lib/auth/session/session";
import request, { isAxiosError } from "@Lib/request";
import {
  createFinishSilentRefreshListener,
  isAxiosRefreshTokenError,
} from "@Utils/helpers";
import Debug from "@Utils/debug";

const debug = Debug("hooks:useTotals");
export interface UseTotalsConfig {
  facilityCount: number;
  revalidate: boolean;
}

const calculateFacilities = (facilityCount: number) => {
  const perFacility = 11;
  return facilityCount * perFacility;
};

const calculate = (
  products: Product[],
  facilityCount: number = 0,
  taxRate: number = 0,
): CartResponse => {
  const pTotal = products.reduce((prev, curr) => {
    if (!getItemRequirements(curr).includes(CartItemRequirementType.Medical)) {
      prev = prev + curr.Price!;
    }
    return prev;
  }, 0);
  const fTotal = calculateFacilities(facilityCount);
  const subtotal = pTotal + fTotal;
  const tax = subtotal * taxRate;
  const total = subtotal + tax;
  return {
    Items: [],
    Discounts: [],
    SubTotal: subtotal,
    Tax: tax,
    Total: total,
  };
};

const getCartTotals = async (
  cartRequest: CartRequest,
): Promise<CartResponse | undefined> => {
  try {
    const { data } = await request.post<CartResponse>("/api/cart", cartRequest);
    return data;
  } catch (err) {
    if (isAxiosRefreshTokenError(err)) {
      const prom = new Promise<CartResponse | undefined>(resolve => {
        createFinishSilentRefreshListener(async () => {
          resolve(await getCartTotals(cartRequest));
        });
      });

      return await prom;
    }

    if (isAxiosError(err)) {
      debug.error("Error fetching cart totals", {
        status: err.response?.status,
        message: err.message,
      });
    }
  }
};

export interface UseLocalTotalResult {
  totals: CartResponse;
}

export interface UseTotalsResult {
  totals: CartResponse;
  fetching: boolean;
  error: boolean;
}

export interface UseTotalsOptions {
  clientId?: number;
  products: Product[];
  facilityCount?: number;
}

export const useTotals = (
  request?: UseTotalsOptions,
  initialState?: CartResponse,
  // client: ISessionClient | null | undefined,
): UseTotalsResult => {
  const [totals, setTotals] = React.useState(() =>
    initialState ? initialState : defaultCartTotals,
  );
  const [fetching, setFetching] = React.useState(false);
  const [error, setError] = React.useState(false);
  const timer: React.MutableRefObject<NodeJS.Timeout | null> =
    React.useRef(null);

  React.useEffect(() => {
    let active = true;
    if (!request) return;

    if (
      request.products.length === 0 ||
      (request.products.filter(v => !v.IsMedical).length === 0 &&
        request.facilityCount === 0)
    ) {
      return setTotals(defaultCartTotals);
    }

    const r: CartRequest = {
      ClientId: request.clientId,
      ProductIds: request.products.map(p => +p.Id),
      FacilityCount: request.facilityCount,
    };

    debug.debug("In Effect: %o", r);
    if (active) {
      setFetching(true);

      timer.current = setTimeout(() => {
        (async () => {
          debug.debug("Making request: %o", r);

          const fetched = await getCartTotals(r);
          if (fetched) {
            setTotals(fetched);
          } else {
            setError(true);
          }
          setFetching(false);
        })();
      }, 2000);
    }

    return () => {
      if (timer.current) clearTimeout(timer.current);
      active = false;
    };
  }, [request]);

  return { totals, fetching, error };
};

export const useLocalTotals = (
  request: CartRequest,
  client: ISessionClient | null | undefined,
  validate: boolean = false,
): UseLocalTotalResult => {
  const { items } = useOrder();
  // const {data: taxRate} = useRequest<number>({
  //   url: "/api/clients/me/taxrate"
  // });

  const [state, setState] = React.useState<CartResponse>(defaultCartTotals);

  React.useEffect(() => {
    setState(calculate(items, request.FacilityCount, client?.TaxRate || 0));
  }, [items, request, client, validate]);

  return {
    totals: state,
  };
};

export const defaultCartTotals: CartResponse = {
  Items: [],
  Discounts: [],
  SubTotal: 0,
  Total: 0,
  Tax: 0,
};

export default useLocalTotals;
